import Description from 'components_v1/ClassView/Description'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import BuilderSection from 'components_v1/BuilderSection'
import StyledVideoPlayer from 'components_v1/v2Wrappers/StyledVideoPlayer'
import { ResponsiveSVG } from 'ui/acro'
import PlayIcon from '../../../assets/codicons/play.svg'
import GuideText from 'components_v1/ClassView/GuideText'
import TagsContainer from 'components_v1/ClassView/TagsContainer'
import {
  DifficultyContainer,
  IntensityContainer,
  StyleSection
} from 'components_v1/ClassView/DifficultyIntensity'
import { useElementSize } from 'hooks/useElementSize'
import PlanCoachInfo from './PlanCoachInfo'
import { ProgramViewContext } from 'components_v2/page_context'

const ProgramMain = () => {
  const { plan, bonus, introVideo, user, isMobile } =
    useContext(ProgramViewContext)
  const [readMoreNeeded, setReadMoreNeeded] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const { height: introVideoHeight } = useElementSize(
    document.querySelector('.plan-video-container .intro-video')
  )
  const [defaultDescHeight, setDefaultDescHeight] = useState()
  const initDescHeight = useMemo(
    () =>
      Math.min(
        250,
        introVideoHeight - 107 > 0 &&
          window.matchMedia('(min-width: 767px)').matches
          ? introVideoHeight - 107
          : 250
      ),
    [introVideoHeight]
  )

  useEffect(() => setDefaultDescHeight(initDescHeight), [initDescHeight])
  const descriptionRef = useRef(null)
  useEffect(() => {
    const el = descriptionRef.current
    if (el && el.scrollHeight > el.clientHeight) {
      setReadMoreNeeded(true)
    }
  }, [introVideoHeight])

  const toggleExpand = (e) => {
    e.preventDefault()
    const el = descriptionRef.current
    if (!el) return
    if (expanded) {
      setDefaultDescHeight(initDescHeight)
      requestAnimationFrame(() => {
        el.style.height = ''
      })
    } else {
      setDefaultDescHeight(
        document?.getElementById('desc-content')?.scrollHeight
      )
      const onEnd = () => {
        el.style.height = 'auto'
        el.removeEventListener('transitionend', onEnd)
      }
      el.addEventListener('transitionend', onEnd)
    }
    setExpanded((prev) => !prev)
  }
  const localizedIntroVideos = plan?.localizedIntroVideos || []
  return (
    <div className='bundle-sidebar plan-sidebar product-sidebar'>
      <div className='top-section-desktop'>
        <div className='about-container'>
          <div className='plan-information'>
            <h2>Description</h2>
            <div
              ref={descriptionRef}
              className={`plan-description${
                readMoreNeeded && !expanded ? ' readmore-needed' : ''
              }`}
              style={{
                overflow: 'hidden',
                transition: 'height 0.3s ease',
                ...(readMoreNeeded
                  ? {
                      maxHeight: `${Math.floor(defaultDescHeight)}px`
                    }
                  : {})
              }}
            >
              <div id='desc-content' className='plan-description-paragraphs'>
                <Description description={plan?.description} />
              </div>
            </div>
            <div className='description-readmore-expand'>
              {readMoreNeeded && (
                <a
                  href='#/'
                  onClick={toggleExpand}
                  className='readmore-js-toggle desc-readmore'
                >
                  Read {expanded ? 'Less' : 'More'}
                </a>
              )}
            </div>
            {bonus && (
              <div className='bonus-content-section'>
                <BuilderSection
                  data={bonus}
                  user={{}}
                  builderModelname='bonus-content-section'
                />
              </div>
            )}
          </div>
        </div>
        <div className='plan-video-container'>
          {introVideo?.id && (
            <div
              id='intro-video-container'
              className='play-video-container intro-video video-play-cover'
            >
              {introVideo?.processStatus === 'uploaded' ||
              introVideo?.processStatus === 'started' ? (
                <img
                  src='https://d28gwrkukqy4h7.cloudfront.net/training/videoProcessing.jpg'
                  alt='processing'
                />
              ) : (
                <StyledVideoPlayer
                  videoId={introVideo.id}
                  planId={plan.id}
                  user={user}
                  videoOriginType='Plan'
                  {...(localizedIntroVideos?.length > 1 && {
                    localizedVideos: localizedIntroVideos
                  })}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className='grey-info-section'>
        <div className='grey-info-section-content'>
          {!isMobile && (
            <div className='grey-left-desktop'>
              <div className='left-desktop-container'>
                {plan?.coaches?.length > 0 && <PlanCoachInfo plan={plan} />}
              </div>
            </div>
          )}

          <div className='grey-right-desktop'>
            <div className='right-dif-intens-section right-smaller-section'>
              <div className='plan-basic-info'>
                <div
                  className={`dif-row ${
                    plan?.primaryCategory?.id ? '' : 'basic-row-pushed'
                  }`}
                >
                  <StyleSection categories={plan?.categories} />

                  <div className='difficulty-section'>
                    <h4>TOTAL CLASSES</h4>
                    <div className='plan-stats duration'>
                      <div className='icon'>
                        <ResponsiveSVG
                          className='codicon'
                          SVG={PlayIcon}
                          width='20px'
                          height='20px'
                        />
                      </div>
                      <div className='desc'>
                        <span className='total-plan-duration'>
                          {`${plan?.workoutCount} Class${
                            plan?.workoutCount === 1 ? '' : 'es'
                          }`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='dif-row'>
                  <DifficultyContainer
                    difficultyLevel={plan?.difficultyLevel}
                  />
                  <IntensityContainer intensityLevel={plan?.intensityLevel} />
                </div>
                {plan?.intensityLevel && plan?.difficultyLevel && <GuideText />}
              </div>
            </div>
            {plan?.contextTags?.length > 0 && (
              <div className='right-works-on-section right-smaller-section'>
                <div className='tags-container'>
                  <TagsContainer tags={plan?.contextTags} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProgramMain
